import * as R from 'ramda';
import { useEffect, useState } from 'react';
import { Experience, SkillArea, SkillType } from '../API';
import TechnicalSkill, { listMainSkillsWithOpenPositions, listTechnicalSkills } from '../models/technical-skills';
import { Maybe } from '../types';

let techSkillsPromise: Promise<TechnicalSkill[]>;

async function fetchTechnicalSkills() {
  if (techSkillsPromise) return techSkillsPromise;
  techSkillsPromise = listTechnicalSkills();
  return techSkillsPromise;
}

async function fetchOpenJobsTechnicalSkills(experience?: Experience) {
  return listMainSkillsWithOpenPositions(experience);
}

function filterSkills(
  skills: TechnicalSkill[],
  area: Maybe<SkillArea>,
  type: Maybe<SkillType>,
) {
  let resultSkills: TechnicalSkill[] = skills;
  if (area || type) {
    resultSkills = R.filter(R.where({
      area: (x: SkillArea | undefined) => (area ? x === area : true),
      type: (x: SkillType | undefined) => (type ? x === type : true),
    }), resultSkills);
  }
  return resultSkills;
}

type SkillsState = {
  skills: TechnicalSkill[] | undefined;
  fetching: boolean;
};

export default function useTechSkills(
  {
    area,
    type,
    withOpenJobs,
    experienceForOpenJobs,
  }: {
    area?: SkillArea,
    type?: SkillType,
    withOpenJobs?: boolean,
    experienceForOpenJobs?: Experience,
  } = {},
) {
  const [{ skills, fetching }, setSkills] = useState<SkillsState>({
    skills: undefined, fetching: true,
  });
  useEffect(
    () => {
      setSkills({ skills: [], fetching: true });
      const query = withOpenJobs ? fetchOpenJobsTechnicalSkills : fetchTechnicalSkills;
      query(experienceForOpenJobs)
        .then((sks) => sks.sort((a, b) => (a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))))
        .then((sks) => filterSkills(sks, area, type))
        .then((sks) => setSkills({ skills: sks, fetching: false }));
    },
    [area, type, withOpenJobs, experienceForOpenJobs],
  );
  return { skills: fetching ? undefined : skills, fetchingSkills: fetching };
}
